<script setup>
/**
 * The last screen between an owner and thousands of paid WhatsApp messages.
 *
 * FOUR THINGS HAPPEN HERE AND THEY HAPPEN IN THIS ORDER, which is the design:
 *
 *  1. **Read it back.** The approved message with every blank replaced by what it will be
 *     filled from. Not a template — the thing a customer receives.
 *  2. **Count who gets it.** An explicit press, never on change and never on keystroke:
 *     counting walks every page of the audience upstream and is rate-limited at 10/min, so
 *     a count-as-you-type would exhaust it before the owner finished a phone number. It
 *     spends nothing, and the copy says so, because an owner who is afraid of the button
 *     will guess instead.
 *  3. **Acknowledge the skipped.** Only when somebody is being skipped, and the number is
 *     in the sentence — "37 customers will not receive this" is a fact she can weigh; "some
 *     contacts will be skipped" is not.
 *  4. **Send, behind one more question.** The confirmation replaces the button IN PLACE
 *     rather than in a dialog, so the count and the skip reasons it is about are still on
 *     screen above it — the same treatment task removal gets, for the same reason.
 *
 * `isStale` IS THE CLIENT HALF OF THE SERVER'S FIRST SPEND GATE. The BFF refuses a send
 * whose draft no longer fingerprints to the previewed state, and it is right to — but being
 * refused after pressing Send on a screen that looked ready is a bad way to learn that
 * going back and adding a condition invalidated the count. So the page tracks the same
 * thing locally and the send control is replaced by "count again" the moment the draft
 * moves. The server gate stays; this only stops it from ever being the first the owner
 * hears of it.
 */
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

import { useWasloI18n } from '../../i18n.js';
import { toIntlLocale } from '../../helpers/intl.js';
import { skipReasonText } from '../../helpers/skip-reasons.js';

const props = defineProps({
  /** The message with its blanks already substituted, from `renderTemplateBody`. */
  renderedBody: { type: String, default: '' },
  /** `wasloApi.previewBroadcast` output, or null before the first count. */
  preview: { type: Object, default: null },
  isCounting: { type: Boolean, default: false },
  isSending: { type: Boolean, default: false },
  /** True when the draft changed after the count that is on screen. */
  isStale: { type: Boolean, default: false },
  /** One sentence from the server, rendered as it was written. */
  errorMessage: { type: String, default: '' },
});

const emit = defineEmits(['count', 'send']);

const { t, locale } = useWasloI18n();

const acknowledged = ref(false);
const confirming = ref(false);

const num = value =>
  new Intl.NumberFormat(toIntlLocale(locale.value)).format(value);

// A new count is a new decision. Carrying a tick across one would let an acknowledgement
// made for 37 skipped contacts stand for a re-resolved 900 — the exact substitution the
// server's second gate exists to refuse, made impossible on this side too.
//
// KEYED ON THE COUNT STARTING, not only on the result arriving. A watcher on `preview`
// alone compares by REFERENCE, so a second count that happened to return the same object
// would leave the tick standing — which is a fragile thing to rest a spend gate on. The
// moment a count is in flight the number on screen is about to be replaced, and a tick
// made against a number that is being replaced is void.
watch([() => props.preview, () => props.isCounting], () => {
  acknowledged.value = false;
  confirming.value = false;
});
watch(
  () => props.isStale,
  stale => {
    if (stale) confirming.value = false;
  }
);

const willReceive = computed(() => props.preview?.willReceive ?? 0);
const willBeSkipped = computed(() => props.preview?.willBeSkipped ?? 0);

const receiveLine = computed(() =>
  willReceive.value === 1
    ? t('WASLO.BROADCASTS.COMPOSE.CHECK.WILL_RECEIVE_ONE')
    : t('WASLO.BROADCASTS.COMPOSE.CHECK.WILL_RECEIVE', {
        count: num(willReceive.value),
      })
);

const skippedLine = computed(() =>
  willBeSkipped.value === 1
    ? t('WASLO.BROADCASTS.COMPOSE.CHECK.SKIPPED_ONE')
    : t('WASLO.BROADCASTS.COMPOSE.CHECK.SKIPPED', {
        count: num(willBeSkipped.value),
      })
);

const ackLine = computed(() =>
  willBeSkipped.value === 1
    ? t('WASLO.BROADCASTS.COMPOSE.CHECK.ACK_ONE')
    : t('WASLO.BROADCASTS.COMPOSE.CHECK.ACK', {
        count: num(willBeSkipped.value),
      })
);

const confirmTitle = computed(() =>
  willReceive.value === 1
    ? t('WASLO.BROADCASTS.COMPOSE.CHECK.CONFIRM_TITLE_ONE')
    : t('WASLO.BROADCASTS.COMPOSE.CHECK.CONFIRM_TITLE', {
        count: num(willReceive.value),
      })
);

/**
 * The dead end, and there are two of them: an audience that matched NOBODY is a conditions
 * problem, and one where everybody was skipped is a phone-number or blanks problem. Sending
 * the owner to the wrong one costs her another minute-long count.
 */
const deadEnd = computed(() => {
  if (!props.preview || willReceive.value > 0) return '';
  return willBeSkipped.value === 0
    ? t('WASLO.BROADCASTS.COMPOSE.CHECK.NOBODY_MATCHED')
    : t('WASLO.BROADCASTS.COMPOSE.CHECK.NOBODY_LEFT');
});

// NO SEND CONTROL AT ALL when there is nobody to send to — not a disabled one. A disabled
// button is one devtools attribute away from launching a broadcast with no recipients.
const canSend = computed(
  () =>
    Boolean(props.preview) &&
    !props.isStale &&
    willReceive.value > 0 &&
    (willBeSkipped.value === 0 || acknowledged.value)
);

const send = () => {
  confirming.value = false;
  emit('send', acknowledged.value);
};
</script>

<template>
  <div class="flex flex-col gap-6" data-waslo-send-check>
    <!-- 1 · what a customer receives -->
    <section class="flex flex-col gap-2">
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('WASLO.BROADCASTS.COMPOSE.CHECK.MESSAGE_HEADING') }}
      </h3>
      <p
        class="px-4 py-3 mb-0 text-sm leading-6 whitespace-pre-line outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1 text-n-slate-12"
        dir="auto"
        data-waslo-send-body
      >
        {{ renderedBody }}
      </p>
      <p class="mb-0 text-xs text-n-slate-10 max-w-[62ch]">
        {{ t('WASLO.BROADCASTS.COMPOSE.CHECK.MESSAGE_NOTE') }}
      </p>
    </section>

    <!-- 2 · how many people -->
    <section class="flex flex-col gap-3">
      <div v-if="!preview || isStale" class="flex flex-col items-start gap-2">
        <p
          v-if="isStale"
          class="mb-0 text-sm text-n-amber-11"
          data-waslo-send-stale
        >
          {{ t('WASLO.BROADCASTS.COMPOSE.CHECK.STALE') }}
        </p>
        <Button
          :label="
            isCounting
              ? t('WASLO.BROADCASTS.COMPOSE.CHECK.COUNTING')
              : t('WASLO.BROADCASTS.COMPOSE.CHECK.COUNT_ACTION')
          "
          variant="outline"
          color="slate"
          size="sm"
          :is-loading="isCounting"
          :disabled="isCounting"
          data-waslo-send-count
          @click="emit('count')"
        />
        <p class="mb-0 text-sm text-n-slate-11 max-w-[62ch]">
          {{ t('WASLO.BROADCASTS.COMPOSE.CHECK.COUNT_NOTE') }}
        </p>
      </div>

      <div
        v-else
        class="flex flex-col gap-3 px-5 py-4 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
        data-waslo-send-count-result
      >
        <p
          class="mb-0 text-base font-medium text-n-slate-12"
          data-waslo-send-receive
        >
          {{ receiveLine }}
        </p>

        <div v-if="preview.willBeSkipped > 0" class="flex flex-col gap-2">
          <p class="mb-0 text-sm text-n-slate-11" data-waslo-send-skipped>
            {{ skippedLine }}
          </p>
          <ul
            v-if="preview.skipReasons.length"
            class="flex flex-col gap-1 mb-0 list-none"
          >
            <li
              v-for="reason in preview.skipReasons"
              :key="reason.code ?? reason.reason"
              class="text-sm text-n-slate-11"
              dir="auto"
            >
              {{
                t('WASLO.BROADCASTS.COMPOSE.CHECK.SKIP_REASON', {
                  count: num(reason.count),
                  reason: skipReasonText(reason, t),
                })
              }}
            </li>
          </ul>
          <p v-else class="mb-0 text-sm text-n-slate-10">
            {{ t('WASLO.BROADCASTS.COMPOSE.CHECK.NO_REASONS') }}
          </p>
          <!-- The total is exact and the reasons are a bounded sample. Two different kinds
               of number; saying so is what stops "2 × no phone number" from reading as the
               whole story beneath a total of 900. -->
          <p
            v-if="preview.reasonsFromSample"
            class="mb-0 text-xs text-n-slate-10 max-w-[62ch]"
          >
            {{
              t('WASLO.BROADCASTS.COMPOSE.CHECK.SAMPLE_NOTE', {
                count: num(preview.sampleSize),
              })
            }}
          </p>
        </div>

        <p v-if="deadEnd" class="mb-0 text-sm text-n-ruby-9" role="alert">
          {{ deadEnd }}
        </p>
      </div>
    </section>

    <!-- 3 + 4 · acknowledge, then send behind one more question -->
    <section
      v-if="preview && !isStale && willReceive > 0"
      class="flex flex-col gap-4"
    >
      <label
        v-if="willBeSkipped > 0"
        class="flex items-start gap-3 text-sm cursor-pointer text-n-slate-12"
      >
        <span class="mt-0.5 shrink-0">
          <Checkbox v-model="acknowledged" data-waslo-send-ack />
        </span>
        <span class="max-w-[62ch]">{{ ackLine }}</span>
      </label>

      <div
        v-if="confirming"
        class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 outline-n-ruby-8 rounded-xl bg-n-solid-1"
        data-waslo-send-confirm
      >
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ confirmTitle }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11 max-w-[62ch]">
          {{ t('WASLO.BROADCASTS.COMPOSE.CHECK.CONFIRM_BODY') }}
        </p>
        <div class="flex flex-wrap items-center gap-3">
          <Button
            :label="t('WASLO.BROADCASTS.COMPOSE.CHECK.CONFIRM_SEND')"
            variant="solid"
            color="ruby"
            size="sm"
            :is-loading="isSending"
            :disabled="isSending"
            data-waslo-send-confirm-yes
            @click="send"
          />
          <Button
            :label="t('WASLO.BROADCASTS.COMPOSE.CHECK.CONFIRM_CANCEL')"
            variant="faded"
            color="slate"
            size="sm"
            :disabled="isSending"
            @click="confirming = false"
          />
        </div>
      </div>

      <Button
        v-else-if="canSend"
        :label="
          isSending
            ? t('WASLO.BROADCASTS.COMPOSE.CHECK.SENDING')
            : t('WASLO.BROADCASTS.COMPOSE.CHECK.SEND')
        "
        variant="solid"
        color="blue"
        class="self-start"
        :is-loading="isSending"
        :disabled="isSending"
        data-waslo-send
        @click="confirming = true"
      />

      <p class="mb-0 text-xs text-n-slate-10 max-w-[62ch]">
        {{ t('WASLO.BROADCASTS.COMPOSE.CHECK.RESOLVE_NOTE') }}
      </p>
    </section>

    <!--
      The server's own sentence, printed as it was written. The two spend gates and the
      launch-failure copy live on the BFF precisely so there is ONE wording of each; a
      translation here would be a second one, and the whole point of that copy is that it
      never says "nothing was changed" about a send it could not confirm.
    -->
    <p
      v-if="errorMessage"
      class="mb-0 text-sm text-n-ruby-9 max-w-[68ch]"
      role="alert"
      data-waslo-send-error
    >
      {{ errorMessage }}
    </p>
  </div>
</template>
